export type CandidateEducationInput = {
  school_name?: string | null;
  school?: string | null;
  school_domain?: string | null;
  display_name?: string | null;
  degrees?: readonly string[] | null;
  degree?: string | null;
  majors?: readonly string[] | null;
  field?: string | null;
  major?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  is_current?: boolean | null;
  gpa?: number | null;
};

export type CandidateEducation = {
  schoolName: string;
  schoolDomain: string | null;
  degree: string | null;
  field: string | null;
  startDate: string | null;
  endDate: string | null;
  isCurrent: boolean;
  source: CandidateEducationInput;
};

/**
 * Broker-sourced `degrees` arrays frequently look like
 * `["bs", "ms", "mba"]` (or worse: `["BS Cyber", "MS Cryptography", "MBA"]`).
 * Joining all of them into a single title is noisy, so compact profile
 * surfaces use the top credential.
 */
const DEGREE_TIERS: { pattern: RegExp; rank: number }[] = [
  {
    pattern:
      /\b(ph\.?d|doctor(ate|al)?|dphil|d\.?phil|d\.?sc|sc\.?d|ed\.?d|j\.?d|m\.?d)\b/i,
    rank: 5,
  },
  {
    pattern:
      /\b(m\.?b\.?a|ll\.?m|m\.?f\.?a|m\.?eng|m\.?sc|m\.?s|m\.?a|masters?|post[- ]?graduate)\b/i,
    rank: 4,
  },
  {
    pattern:
      /\b(b\.?s|b\.?a|b\.?eng|b\.?sc|b\.?f\.?a|bachelor'?s?|undergrad(uate)?)\b/i,
    rank: 3,
  },
  { pattern: /\b(a\.?a|a\.?s|associate'?s?)\b/i, rank: 2 },
  {
    pattern: /\b(certific(ate|ation)|diploma|high school|secondary|g\.?e\.?d)\b/i,
    rank: 1,
  },
];

function degreeRank(raw: string): number {
  for (const { pattern, rank } of DEGREE_TIERS) {
    if (pattern.test(raw)) return rank;
  }
  return 0;
}

export function pickHighestDegree(
  degrees: readonly string[] | null | undefined,
): string | undefined {
  if (!degrees?.length) return undefined;
  const normalized = degrees.map((d) => d?.trim()).filter(Boolean) as string[];
  if (!normalized.length) return undefined;

  let bestIdx = 0;
  let bestRank = degreeRank(normalized[0]);
  for (let i = 1; i < normalized.length; i++) {
    const rank = degreeRank(normalized[i]);
    if (rank > bestRank) {
      bestRank = rank;
      bestIdx = i;
    }
  }
  return normalized[bestIdx];
}

function firstNonEmpty(values: Array<string | null | undefined>): string | null {
  for (const value of values) {
    const trimmed = value?.trim();
    if (trimmed) return trimmed;
  }
  return null;
}

function firstArrayValue(values: readonly string[] | null | undefined): string | null {
  return firstNonEmpty([...(values ?? [])]);
}

function dateScore(value: string | null): number {
  if (!value) return Number.NEGATIVE_INFINITY;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
}

function richnessScore(education: CandidateEducation): number {
  let score = 0;
  if (education.schoolDomain) score += 1;
  if (education.degree) score += 1;
  if (education.field) score += 1;
  if (education.startDate) score += 1;
  if (education.endDate) score += 1;
  if (education.source.gpa != null) score += 1;
  return score;
}

function compareEducation(a: CandidateEducation, b: CandidateEducation): number {
  if (a.isCurrent !== b.isCurrent) return a.isCurrent ? -1 : 1;

  const aInProgress = Boolean(a.startDate && !a.endDate);
  const bInProgress = Boolean(b.startDate && !b.endDate);
  if (aInProgress !== bInProgress) return aInProgress ? -1 : 1;

  const endDiff = dateScore(b.endDate) - dateScore(a.endDate);
  if (endDiff !== 0) return endDiff;

  const startDiff = dateScore(b.startDate) - dateScore(a.startDate);
  if (startDiff !== 0) return startDiff;

  return richnessScore(b) - richnessScore(a);
}

function transformEducation(source: CandidateEducationInput): CandidateEducation {
  const degree =
    pickHighestDegree(source.degrees) ??
    firstNonEmpty([source.degree]) ??
    null;
  const field =
    firstArrayValue(source.majors) ??
    firstNonEmpty([source.field, source.major]) ??
    null;
  const startDate = firstNonEmpty([source.start_date]);
  const endDate = firstNonEmpty([source.end_date]);

  return {
    schoolName: firstNonEmpty([
      source.display_name,
      source.school_name,
      source.school,
    ]) ?? "",
    schoolDomain: firstNonEmpty([source.school_domain]),
    degree,
    field,
    startDate,
    endDate,
    isCurrent: source.is_current === true,
    source,
  };
}

export function transformEducations(
  education: readonly CandidateEducationInput[] | null | undefined,
): CandidateEducation[] {
  return [...(education ?? [])]
    .map(transformEducation)
    .sort(compareEducation);
}

export function selectTopEducation(
  education: readonly CandidateEducationInput[] | null | undefined,
): CandidateEducation | undefined {
  return transformEducations(education)[0];
}
